import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";

import { cn } from "@/lib/utils";
import { usePortalContainer } from "@/contexts/portal";

// `modal` prop set to false here to stop annoying window scrollbar disappearing
const DropdownMenu: React.FC<DropdownMenuPrimitive.DropdownMenuProps> = ({
    ...props
}) => <DropdownMenuPrimitive.Root modal={false} {...props} />;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = ({
    className,
    inset,
    children,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
}) => (
    <DropdownMenuPrimitive.SubTrigger
        className={cn(
            menuItemClassName,
            "focus:bg-accent data-[state=open]:bg-selected-default data-[state=open]:hover:bg-selected-hover data-[state=open]:text-selected",
            inset && "pl-8",
            className,
        )}
        {...props}
    >
        {children}
        <ChevronRight className="ml-auto" />
    </DropdownMenuPrimitive.SubTrigger>
);
DropdownMenuSubTrigger.displayName =
    DropdownMenuPrimitive.SubTrigger.displayName;

const menuContentClassName = cn(
    "rounded-default bg-elevation-surface-default p-narrow data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden shadow-overlay",
);

const menuItemClassName = cn(
    "rounded-default px-default py-narrow flex cursor-default items-center gap-2 outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "font-body text-default leading-default text-default-color font-bold", // BodyLabel styles
);

const DropdownMenuSubContent = ({
    className,
    children,
    emptyMessage,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent> & {
    emptyMessage?: string;
}) => (
    <DropdownMenuPrimitive.SubContent
        className={cn(menuContentClassName, className)}
        {...props}
    >
        <div className="peer">{children}</div>
        {emptyMessage && (
            <DropdownMenuItem className="hidden peer-empty:block">
                {emptyMessage}
            </DropdownMenuItem>
        )}
    </DropdownMenuPrimitive.SubContent>
);
DropdownMenuSubContent.displayName =
    DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = ({
    className,
    sideOffset = 4,
    children,
    emptyMessage,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
    emptyMessage?: string;
}) => {
    const container = usePortalContainer();
    return (
        <DropdownMenuPrimitive.Portal container={container}>
            <DropdownMenuPrimitive.Content
                sideOffset={sideOffset}
                className={cn(
                    menuContentClassName,
                    "min-w-(--radix-dropdown-menu-trigger-width)",
                    className,
                )}
                {...props}
            >
                <div className="peer">{children}</div>
                {emptyMessage && (
                    <DropdownMenuItem className="hidden peer-empty:block">
                        {emptyMessage}
                    </DropdownMenuItem>
                )}
            </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
    );
};
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = ({
    className,
    inset,
    ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
}) => (
    <DropdownMenuPrimitive.Item
        className={cn(
            menuItemClassName,
            "hover:bg-neutral-subtlest-hover active:bg-neutral-subtlest-press data-disabled:bg-elevation-surface-default data-disabled:text-disabled data-[selected=true]:bg-selected-default data-[selected=true]:hover:bg-selected-hover data-[selected=true]:active:bg-selected-pressed data-[selected=true]:text-selected relative cursor-pointer transition-colors hover:ring-0 data-disabled:pointer-events-none",
            inset && "pl-8",
            className,
        )}
        {...props}
    />
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = ({
    className,
    children,
    checked,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => (
    <DropdownMenuPrimitive.CheckboxItem
        className={cn(
            "rounded-default data-disabled:text-disabled relative flex cursor-default items-center py-1.5 pr-2 pl-8 outline-hidden transition-colors select-none data-disabled:pointer-events-none",
            className,
        )}
        checked={checked}
        {...props}
    >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <Check className="h-4 w-4" />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.CheckboxItem>
);
DropdownMenuCheckboxItem.displayName =
    DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = ({
    className,
    children,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => (
    <DropdownMenuPrimitive.RadioItem
        className={cn(
            "relative flex cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 outline-hidden transition-colors select-none data-disabled:pointer-events-none data-disabled:opacity-50",
            className,
        )}
        {...props}
    >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <Circle className="h-2 w-2 fill-current" />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.RadioItem>
);
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = ({
    className,
    inset,
    ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
}) => (
    <DropdownMenuPrimitive.Label
        className={cn(
            "text-small px-2 py-1.5 font-bold",
            inset && "pl-8",
            className,
        )}
        {...props}
    />
);
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = ({
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
    <DropdownMenuPrimitive.Separator
        className={cn("bg-neutral-subtle-default -mx-1 my-1 h-px", className)}
        {...props}
    />
);
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
    return (
        <span
            className={cn("ml-auto tracking-widest opacity-60", className)}
            {...props}
        />
    );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuRadioItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuGroup,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuRadioGroup,
};
